import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { formattedDateTime, getCurrentDateTime } from 'pages/utils/commonUtils';
import { PrimaryButton, PrimaryPinkButton, WhiteButton } from "components/common/Button";
import { Jisa } from "type/type";
import { maxLengthCheck, getJisaOne, postJisa, putJisa, deleteJisa } from "api/api";
import Loading from "components/common/Loading";
import { useForm } from "react-hook-form";


const Container = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 0.5rem;
  > div {
    padding: 0.5rem 1rem 1rem;
    &:last-of-type {
      background-color: ${prop => prop.theme.layer3BgColor};
      border-radius: 0.5rem;
    }
  }
  .title-h2 {
    font-family: var(--font-pretendard-bold);
    display: inline-flex;
    flex-direction: row;
    gap: 0.5rem;
    align-items: center;
  }

  .process-content {
    > div {
      max-width: 700px;
    }
    input {
      min-width: 150px;
      max-width: 400px;
      // width: 50%;
    }

    .select-wrap {
      position: relative;
      min-width: 150px;
      max-width: 400px;
      width: 100%;

      &::after {
        content: '';
        position: absolute;
        width: 0;
        height: 0;
        top: 50%;
        right: 20px;
        border: 5px solid transparent;
        border-top-color: #313a43;
        transform: scaleY(1.4);
      }
      &::before {
        content: '';
        position: absolute;
        display: block;
        width: 1px;
        height: 95%;
        top: 0;
        right: 48px;
        background-color: #e7e7e7;
        z-index: 0;
      }
    }
  }
  
  .process-content2 {
    > div {
      max-width: 1400px;
    }
  }

  .err-border {
    border: 1px solid red !important;
  }
`;

const TitleAndBtnContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  > div {
    display: flex;
    gap: 0.5rem;
    &:first-of-type {
      flex-direction: column;
    }
    &:last-of-type {
      align-items: center;
    }
  }
`;

const FlexCol = styled.div`
  display: flex;
  flex-direction: column;
`;
const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
`;

const InputWrap = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0.5rem;
  gap: 1rem;
  // FIXME: 다른 페이지 디자인대로 하려면 아래 주석 풀면 됨
  // border-bottom: 1px solid #666;

  label {
    width: 6rem;
  }
  label, input, select, textarea {
    display: inline-block;
    padding: 0.5rem;
  }
  textarea {
    resize: none;
    min-height: 10rem;
  }
  input:not([type="radio"]), textarea {
    width: calc(100% - 6rem);
    border: none;
    background-color: #f1f1f1;
    border-radius: 0.25rem;
    &:focus-visible {
      outline: none;
      &:not(:disabled, :read-only) {
        box-shadow: inset -1px -1px 1px 1px ${prop => prop.theme.primaryColor};
      }
    }
    &:read-only {
      background-color: inherit;
      border-bottom: 1px solid #666;
      border-radius: 0;
      // FIXME: 다른 페이지 디자인대로 하려면 아래 주석 풀면 됨
      // border-radius: 0.25rem;
      // background-color: ${prop => prop.theme.readOnlyColor}; 
      color: ${prop => prop.theme.rootTextColor};
      cursor: auto;
    }
  }
`;

function AfterServiceProcess() {
  // 등록/수정 시 전달 값, url
  const navigate = useNavigate();
  // 목록에서 전달 받은 값
  const { jisaIdx } = useParams();
  const [loading, setLoading] = useState(true);
  
  // useForm
  const { register, formState: { errors, isValid }, trigger } = useForm<any>({
    mode: 'all',
  });

  const currentDateTime = getCurrentDateTime();
  const [jisaData, setJisaData] = useState<Jisa>({
    idx: jisaIdx ? Number(jisaIdx) : 0,
    jisa_code: '',
    jisa_name: '',
    bonbu_name: '',
    bigo: '',
    create_date: currentDateTime,
  });
  
  // JISA 정보 변경
  const handleChange = (event: any) => {
    maxLengthCheck(event);
    const { name, value } = event.target;
    setJisaData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  
  // JISA 등록
  const _createJisa = async () => {
    if (!isValid) {
      trigger();
      return;
    }

    const response = await postJisa(jisaData);
    if (response.code === 200) {
      alert("지사가 등록되었습니다.");
      navigate(-1);
    } else {
      alert(response.message);
    }
  };
  
  // JISA 삭제
  const _deleteJisa = async () => {
    const isDel = window.confirm(`${jisaData.jisa_name}을 정말로 삭제하시겠습니까?`);
    if (isDel) {
      const response = await deleteJisa(Number(jisaIdx));
      if (response.code === 200) {
        alert("지사 정보가 삭제되었습니다..");
        navigate(-1);
      } else {
        alert(response.message);
      }
    }
  };

  // JISA 수정
  const _editJisa = async () => {
    if (!isValid) {
      trigger();
      return;
    }

    const response = await putJisa(jisaData);
    if (response.code === 200) {
      alert("지사 정보가 수정되었습니다.");
      navigate(-1);
    } else {
      alert(response.message);
    }
  };

  // AS 리스트 가져오기
  const getJisa = useCallback(async () => {
    try {
      setLoading(true); // 🔥 요청 시작 전에 딩 설정
      if (!jisaIdx) {
        return;
      }
      const jisaRes = await getJisaOne(Number(jisaIdx));
      if (jisaRes.code === 200) {
        setJisaData(jisaRes.data);
      } else {
        alert(jisaRes.message);
      }
  
    } catch (error) {
      console.error("error", error);
    } finally {
      setLoading(false); // 🔥 요청이 끝나면 무조건 로딩 종료
    }
  }, [jisaIdx, navigate]);

  // 처음 useEffect 실행 및 페이지 변경 시 실행
  useEffect(() => {
    getJisa();
  }, [getJisa]);
  return (
    <>
    {loading ? <Loading /> :
      <Container>
        <TitleAndBtnContainer>
          <div>
            <p className='title-h2'>
              <span></span>
            </p>
          </div>
          <div>
            {!jisaIdx ?
              <PrimaryButton type="button" onClick={_createJisa}>저장</PrimaryButton>
            :
              <>
                <PrimaryButton type="button" onClick={_editJisa}>저장</PrimaryButton>
                <PrimaryPinkButton type="button" onClick={_deleteJisa}>삭제</PrimaryPinkButton>
              </>
            }
            <WhiteButton type="button" onClick={() => navigate(-1)}>목록</WhiteButton>
          </div>
        </TitleAndBtnContainer>
        <FlexCol>
          <FlexRow className="process-content">
            <InputWrap>
              <label>본부명</label>
              <input type='text' value={jisaData.bonbu_name} className={`${errors.bonbu_name && 'err-border'}`} placeholder="본부명을 입력해주세요." maxLength={50}
                {...register("bonbu_name", {
                  required: {
                    value: true,
                    message: '본부명을 입력해주세요.'
                  },
                  onChange: handleChange
                })}
              />
            </InputWrap>
            <InputWrap>
              <label>등록일자</label>
              <input type='text' value={ formattedDateTime(jisaData.create_date) } readOnly />
            </InputWrap>
          </FlexRow>
          <FlexRow className="process-content">
            <InputWrap>
              <label>지사명</label>
              <input type='text' value={ jisaData.jisa_name } className={`${errors.jisa_name && 'err-border'}`} placeholder="지사명을 입력해주세요." maxLength={45}
                {...register("jisa_name", {
                  required: {
                    value: true,
                    message: '지사명을 입력해주세요.'
                  },
                  onChange: handleChange
                })}
              />
            </InputWrap>
            <InputWrap>
              <label>지사 코드</label>
              <input type='text' value={ jisaData.jisa_code} className={`${errors.jisa_code && 'err-border'}`} placeholder="지사 코드를 입력해주세요." maxLength={45} readOnly={Number(jisaIdx || 0) > 0}
                {...register("jisa_code", {
                  required: {
                    value: true,
                    message: '지사 코드를 입력해주세요.'
                  },
                  onChange: handleChange
                })}
              />
            </InputWrap>
          </FlexRow>
          <FlexRow className="process-content2">
            <InputWrap>
              <label>비고</label>
              <input type='text' value={ jisaData.bigo } className={`${errors.bigo && 'err-border'}`} placeholder="비고를 입력해주세요." maxLength={200}
                {...register("bigo", {
                  required: false,
                  onChange: handleChange
                })}
              />
            </InputWrap>
          </FlexRow>
        </FlexCol>
      </Container>
    }
    </>
  );
}

export default AfterServiceProcess;